// Evaluation.tsx
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
    Users2,
    TrendingUp,
} from "lucide-react";

import { PrincipleTabs } from "./PrincipleTabs";
import {
    useEvaluationQuestionsQuery,
    useEvaluationResponsesQuery,
    usePanopticScoringKeyWordAndLikertScaleQuery,
    useProjectEvaluatorsQuery,
} from "../../core/_queries";
import { EvaluationDetails, Principle } from "../../core/_models";
import { getInitials, toTitleCase } from "../../../../../../utils/config";
import SimpleLoader from "../../../../apps/components/SimpleLoader";
import { getStatusConfig } from "../../core/_utils";
import { useAuth } from "../../../../auth";

interface EvaluationProps {
    evaluationPrinciples: Principle[];
    projectId: number;
}

function renderStatusIcon(
    status?: string,
    size: number = 16,
    className: string = ""
): ReactNode {
    const config = getStatusConfig(status);
    const IconComponent = config.icon;

    return (
        <IconComponent
            size={size}
            className={className || `text-${config.color}`}
        />
    );
}

const EvaluatorStatusBadge = ({ status }: { status?: string }) => {
    const config = getStatusConfig(status);
    return (
        <span className={`badge ${config.badge} text-${config.color} px-2 py-1 fs-8 d-flex align-items-center gap-1`}>
            {renderStatusIcon(status, 14, `text-${config.color}`)}
            {toTitleCase(status ?? "Not Started")}
        </span>
    );
};


const Evaluation = ({
    evaluationPrinciples,
    projectId,
}: EvaluationProps) => {

    const { auth } = useAuth()
    const [evaluationDetails, setEvaluationDetails] = useState<EvaluationDetails | null>(null);
    const {
        projectEvaluators,
        isLoading: evaluatorsLoading,
    } = useProjectEvaluatorsQuery(projectId, Number(auth?.user_id));

    const {
        evaluationQuestions,
        isLoading: questionsLoading,
    } = useEvaluationQuestionsQuery(projectId);

    const {
        isLoading: isPanopticScoringKeywords,
        scoringKeywords,
        likertScaleLabels,
    } = usePanopticScoringKeyWordAndLikertScaleQuery();

    const [selectedEvaluator, setSelectedEvaluator] = useState<number>();

    const {
        data: evaluationData,
        isLoading: isEvaluationDetailsLoading,
        isFetching: isEvaluationDetailsFetching,
        refetch: refreshEvaluationDetails,
    } = useEvaluationResponsesQuery(projectId, selectedEvaluator);

    const handleRefreshData = useCallback(async () => {
        await refreshEvaluationDetails();
    }, [refreshEvaluationDetails]);

    useEffect(() => {
        if (evaluationData) {
            setEvaluationDetails(evaluationData);
        }
    }, [evaluationData]);

    const handleUpdateEvaluationDetails = useCallback(async (data: EvaluationDetails) => {
        setEvaluationDetails(data);
    }, []);

    // Select first evaluator when evaluators become available
    useEffect(() => {
        if (
            selectedEvaluator == null &&
            projectEvaluators.length > 0
        ) {
            setSelectedEvaluator(projectEvaluators[0].user_id);
        }
    }, [projectEvaluators, selectedEvaluator]);

    const evaluationOptions = projectEvaluators.map((evaluator) => ({
        label: evaluator.user_name,
        value: evaluator.user_id,
        total_score: evaluator.total_score,
        allow_edit: evaluator.allow_edit,
        eval_status: evaluator.eval_status,
    }));

    // Initial page loader
    if (
        evaluatorsLoading ||
        questionsLoading ||
        isPanopticScoringKeywords
    ) {
        return <SimpleLoader />;
    }

    if (projectEvaluators.length === 0) {
        return (
            <div className="card p-4 shadow-sm">
                <div className="text-center py-4">
                    <p className="text-gray-500">No evaluators available</p>
                </div>
            </div>
        );
    }

    const selected = projectEvaluators.find(
        (e) => e.user_id === selectedEvaluator
    );

    const currentStatusConfig = getStatusConfig(
        evaluationDetails?.eval_status
    );

    return (
        <div className="container-fluid p-0">
            <div className="row g-2">
                {/* Left Panel */}
                {evaluationOptions.length > 1 && (
                    <div className="col-12 col-md-3">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between py-3 px-4">
                                <p className="mb-0 fw-bold text-gray-700 fs-5 d-flex align-items-center gap-2">
                                    <Users2 size={20} className="text-primary me-2" />
                                    Evaluators
                                </p>

                                <span className="badge bg-light-primary text-primary rounded-pill">
                                    {evaluationOptions.length}
                                </span>
                            </div>

                            <div className="card-body p-3">
                                <div className="d-flex flex-column gap-2 p-2" style={{ maxHeight: "45vh", overflowY: "auto" }}>
                                    {evaluationOptions.map((evaluator) => (
                                        <button
                                            key={evaluator.value}
                                            type="button"
                                            className={`evaluator-list-item card p-2 text-start w-100 border-0 transition-all ${selectedEvaluator === evaluator.value
                                                ? "border-primary border-2 shadow-sm"
                                                : "border border-gray-200"
                                                }`}
                                            style={{
                                                transition: "all 0.2s ease",
                                            }}
                                            onClick={() =>
                                                setSelectedEvaluator(evaluator.value)
                                            }
                                        >
                                            <div className="d-flex align-items-center gap-3  justify-content-between ">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div
                                                        className={`d-flex align-items-center justify-content-center rounded-circle fw-bold text-white flex-shrink-0 ${selectedEvaluator === evaluator.value
                                                            ? "bg-primary"
                                                            : "bg-light-primary text-primary"
                                                            }`}
                                                        style={{
                                                            width: 35,
                                                            height: 35,
                                                            fontSize: "14px",
                                                        }}
                                                    >
                                                        {getInitials(evaluator.label)}
                                                    </div>

                                                    <div className="flex-grow-1 min-w-0">
                                                        <p className="mb-0 fw-semibold text-gray-900 fs-7">
                                                            {evaluator.label}
                                                        </p>

                                                        <div className="d-flex align-items-center gap-3 mt-1">
                                                            <span className="text-gray-600 fs-8">
                                                                {evaluator.total_score}% Score
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {EvaluatorStatusBadge({ status: evaluator.eval_status })}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Right Panel */}
                <div
                    className={`col-12 ${evaluationOptions.length > 1 ? "col-md-9" : ""
                        }`}
                >
                    <div className="card shadow-sm">
                        <div className="card-body p-4">
                            {selected ? (
                                <div>
                                    {/* Header */}
                                    <div className="d-flex flex-wrap align-items-center gap-4 mb-4 p-4 bg-white rounded-3 shadow-sm border border-gray-100">
                                        <div className="position-relative">
                                            <div
                                                className="bg-gradient-primary text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: "14px",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {getInitials(selected.user_name)}
                                            </div>
                                        </div>

                                        <div className="flex-grow-1">
                                            <h4 className="fw-bold mb-0 text-gray-900">
                                                {selected.user_name}
                                            </h4>
                                        </div>

                                        <div
                                            className="vr d-none d-lg-block"
                                            style={{ height: 50 }}
                                        />

                                        {/* Score */}
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="bg-light-primary rounded-circle p-2 d-flex align-items-center justify-content-center">
                                                <TrendingUp
                                                    size={20}
                                                    className="text-primary"
                                                />
                                            </div>

                                            <div>
                                                <p className="text-gray-600 fs-8 mb-0">
                                                    Total Score
                                                </p>

                                                <div className="d-flex align-items-baseline gap-1">
                                                    <span className="fs-4 fw-bold text-primary">
                                                        {isEvaluationDetailsLoading ? (
                                                            <span
                                                                className="spinner-border spinner-border-sm text-primary"
                                                                role="status"
                                                            >
                                                                <span className="visually-hidden">
                                                                    Loading...
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            evaluationDetails?.total_score ?? 0
                                                        )}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                className={`bg-light-${currentStatusConfig.color} rounded-circle p-2 d-flex align-items-center justify-content-center`}
                                            >
                                                {isEvaluationDetailsLoading ? (
                                                    <div
                                                        className="spinner-border spinner-border-sm text-primary"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            Loading...
                                                        </span>
                                                    </div>
                                                ) : (
                                                    renderStatusIcon(
                                                        evaluationDetails?.eval_status,
                                                        20
                                                    )
                                                )}
                                            </div>

                                            <div>
                                                <p className="text-gray-600 fs-8 mb-0">
                                                    Status
                                                </p>

                                                {isEvaluationDetailsLoading ? (
                                                    <span className="badge bg-secondary text-white px-2 py-1 fs-8">
                                                        Loading...
                                                    </span>
                                                ) : (
                                                    <span
                                                        className={`badge ${currentStatusConfig.badge} text-${currentStatusConfig.color} px-2 py-1 fs-8 d-flex align-items-center gap-1`}
                                                    >
                                                        {toTitleCase(
                                                            evaluationDetails?.eval_status ??
                                                            "Not Started"
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tabs */}
                                    <div className="position-relative mt-4">
                                        {isEvaluationDetailsFetching &&
                                            !isEvaluationDetailsLoading && (
                                                <div
                                                    className="position-absolute top-0 end-0 m-2"
                                                    style={{ zIndex: 1 }}
                                                >
                                                    <div
                                                        className="spinner-border spinner-border-sm text-primary"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            Refreshing...
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                        {isEvaluationDetailsLoading ? (
                                            <SimpleLoader />
                                        ) : (
                                            <PrincipleTabs
                                                evaluationPrinciples={evaluationPrinciples}
                                                evaluationQuestions={evaluationQuestions}
                                                principle_progress={
                                                    evaluationDetails?.principle_progress ?? []
                                                }
                                                responses={evaluationDetails?.responses ?? []}
                                                allowEdit={
                                                    evaluationDetails?.allow_edit ?? false
                                                }
                                                evaluatorId={
                                                    evaluationDetails?.evaluator_user_id ?? 0
                                                }
                                                projectId={projectId}
                                                scoringKeywords={scoringKeywords}
                                                likertScaleLabels={likertScaleLabels}
                                                onRefreshData={handleRefreshData}
                                                onUpdateEvaluationDetails={handleUpdateEvaluationDetails}
                                                readyToCompleteEvaluation={evaluationDetails?.ready_to_complete_evaluation ?? false}
                                            />
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-gray-500">
                                        No evaluator selected
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { Evaluation };

